/**
 * #547 — the SINGLE source for every operator... er, RESIDENT-facing string on the public
 * /subscribe page. Scanned end-to-end by residentSubscribeVocabulary.lock.test.ts (G6), which
 * runs the shipped forbidden groups (alert-issuance drift · US-ICS · own-family · chat/DM ·
 * raw enums) PLUS resident-specific guards over every constant and every builder output.
 *
 * DOCTRINE (founder-ratified 2026-07-05):
 *   • R3 NOUN: residents subscribe to "emergency notifications / updates from {community}" —
 *     NEVER "alerts" (reads as the reserved Alert Ready broadcast) and never "public
 *     information releases" (operator jargon, §2.2). The platform (NPS) is the PIPE; the
 *     community is the ISSUER — no string may imply NPS issues/sends the emergency content.
 *   • R2 CONSENT: the PIN "proves the phone is yours" — NEVER "required by law" (the CASL
 *     rationale lives in the route/migration comments, not on the resident surface).
 *   • R6 FN PARITY: a First Nation's authority reads identically to a municipality's, via the
 *     shipped residentAuthorityTypeLabel — never a re-minted label.
 */

// ── Page header + trust line (leads the page) ───────────────────────────────────────────
export const PAGE_TITLE = 'Emergency text updates';
export const TRUST_LINE =
  'Get emergency updates by text from your community. NPS runs the messaging; your municipality or First Nation decides what to send. It’s free, opt out any time — emergency and safety updates only, never marketing.';

// ── Step 1 — pick a community ───────────────────────────────────────────────────────────
export const COMMUNITY_STEP_HEADING = 'Find your community';
export const COMMUNITY_SEARCH_PLACEHOLDER = 'Search municipalities and First Nations…';
// Honest empty-state (G5) — we never accept a number for a community we can’t serve.
export const COMMUNITY_EMPTY_STATE =
  'No matching community. Only communities using this service appear here.';
export const COMMUNITY_LOADING = 'Loading communities…';

/**
 * The community-TYPE label shown beside each search result. Uses the ACCURATE per-type label
 * (matching the operator CommunityBody/EvacuationBody maps) — an Inuit community reads "Inuit
 * Community", a Métis settlement "Métis Settlement", NEVER collapsed to "First Nation" (that
 * would be a public jurisdictional mislabel — DM-2, 2026-07-05 review). R6 authority parity
 * (an Inuit/Métis community issuing AS a first_nation-equivalent authority) is a backend concern;
 * the resident's own SMS leads with the community NAME, not the type, so parity is preserved.
 */
const COMMUNITY_TYPE_DISPLAY: Record<string, string> = {
  municipality: 'Municipality',
  first_nation: 'First Nation',
  unincorporated: 'Unincorporated',
  metis_settlement: 'Métis Settlement',
  inuit_community: 'Inuit Community',
  other: 'Community',
};
export function communityTypeLabel(communityType: string): string {
  return COMMUNITY_TYPE_DISPLAY[communityType] ?? 'Community';
}

// ── Step 2 — enter a mobile number ──────────────────────────────────────────────────────
export const PHONE_STEP_HEADING = 'Your mobile number';
export const PHONE_LABEL = 'Mobile number (we’ll add +1 for Canada)';
export const PHONE_PLACEHOLDER = '204-555-1234';
export const PHONE_SUBMIT = 'Text me a code';
export const PHONE_SUBMIT_BUSY = 'Sending code…';
// R2 — the code proves ownership; it is NOT framed as a legal requirement.
export const CONSENT_NOTE = 'We’ll text a 6-digit code to confirm this phone is yours.';

/** Per-community framing once a community is picked — the community is the issuer (R3). */
export function willTextFor(communityName: string): string {
  return `${communityName} will text emergency updates for this community to the number you confirm.`;
}

// ── Step 3 — enter the texted PIN ───────────────────────────────────────────────────────
export const PIN_STEP_HEADING = 'Enter your code';
export const PIN_LABEL = 'Enter the 6-digit code';
export const PIN_PLACEHOLDER = '000000';
export const PIN_SUBMIT = 'Confirm';
export const PIN_SUBMIT_BUSY = 'Confirming…';
export const PIN_EXPIRY_NOTE = 'The code expires in 15 minutes.';
export const PIN_RESEND = 'Resend code';

export function pinSentTo(maskedPhone: string): string {
  return `We texted a 6-digit code to ${maskedPhone}.`;
}
export function pinTriesLeft(n: number): string {
  return `You have ${n} ${n === 1 ? 'try' : 'tries'} left.`;
}
export function pinResendIn(seconds: number): string {
  return `Resend code in ${seconds}s`;
}

// ── Step 4 — success ────────────────────────────────────────────────────────────────────
export const SUCCESS_HEADLINE = 'You’re confirmed.';
export function successBody(communityName: string, maskedPhone: string): string {
  return `${communityName} will text emergency updates to ${maskedPhone}. Reply STOP any time, or unsubscribe here. Standard message rates may apply.`;
}
export const SUCCESS_UNSUBSCRIBE_LINK = 'Unsubscribe';

// ── Navigation ──────────────────────────────────────────────────────────────────────────
export const CHANGE_COMMUNITY = '← Change community';

// ── Unsubscribe ─────────────────────────────────────────────────────────────────────────
export const UNSUBSCRIBE_HEADING = 'Unsubscribe from emergency text updates';
export const UNSUBSCRIBE_PICK_PROMPT = 'Pick your community, then confirm the number to unsubscribe.';
export const UNSUBSCRIBE_TOKEN_PROMPT = 'Confirm below to stop receiving emergency text updates.';
export const UNSUBSCRIBE_SUBMIT = 'Unsubscribe';
export const UNSUBSCRIBE_SUBMIT_BUSY = 'Unsubscribing…';
// No retention plea — an unsubscribe is honored plainly.
export function unsubscribeConfirmed(communityName: string): string {
  return `Done. ${communityName} will no longer text you.`;
}
export const UNSUBSCRIBE_CONFIRMED_GENERIC = 'Done. You will no longer be texted.';

// ── Error / status copy ─────────────────────────────────────────────────────────────────
export const ERR_INVALID_PHONE = 'Enter a mobile number, e.g. 204-555-1234.';
export const ERR_WRONG_CODE = 'That code didn’t match. Check it and try again.';
export const ERR_LOCKED = 'Too many tries. Request a new code.';
export const ERR_EXPIRED = 'That code expired. Request a new code.';
export const ERR_GENERIC = 'Something went wrong. Please try again.';
export const PIN_SENT_TOAST = 'Code sent. Check your phone.';

// ── Footer (reinforces R3: platform = pipe, community = issuer) ──────────────────────────
export const FOOTER_NOTE = 'NPS runs the messaging. Your community decides what to send.';
